"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * A double-linked list of loadable HTML elements
 */
var LazyItem = /** @class */ (function () {
    function LazyItem(itemView, previousItem) {
        this.view = itemView;
        this.next = undefined;
        this.previous = previousItem;
        this.isLoaded = false;
        if (previousItem) {
            previousItem.next = this;
        }
    }
    LazyItem.prototype.activate = function () {
        if (!this.isLoaded) {
            this.view.load();
            this.isLoaded = true;
        }
    };
    LazyItem.prototype.deactivate = function () {
        if (this.isLoaded) {
            this.view.unload();
            this.isLoaded = false;
        }
    };
    LazyItem.prototype.isActive = function () {
        return this.isLoaded;
    };
    LazyItem.prototype.successor = function (direction) {
        return direction > 0 ? this.next : this.previous;
    };
    return LazyItem;
}());
exports.LazyItem = LazyItem;
/**
 * Manage a collection of elements on a page to keep memory usage at a reasonable size.
 *
 * This component is similar to RecyclerView on Android or UICollectionView on iOS.
 *
 * The management algorithm tries to minimize the appearance of unloaded views in active area by
 * pre-loading items in batches. Off-screen views are unloaded to save memory.
 */
var LazyLoading = /** @class */ (function () {
    function LazyLoading(containerElement) {
        this.containerElement = containerElement;
        /**
         * How much is the active area larger than the viewport
         */
        this.sensitivity = 2;
        /**
         * How many items are preloaded at once
         * TODO Calculate dynamically, based on refreshItems stats
         */
        this.batchSize = 10;
        /**
         * Direction of the last pre-fetch operation
         * Stored to avoid trimming pre-fetched items
         */
        this.lastPrefetchDirection = null;
        /**
         * Print diagnostic logs to console
         */
        this.debug = false;
        this.containerElement = containerElement; // TODO Abstract away the HTMLElement
        this.sensitivity = 2;
        this.batchSize = 10;
        this.debug = false;
        this.items = [];
        this.activeRect = null;
        this.lowMarker = undefined;
        this.highMarker = undefined;
        this.lastPrefetchDirection = undefined;
        var _manageItems = this.manageItems.bind(this);
        this.scrollListener = function () { return window.requestAnimationFrame(_manageItems); };
        this.containerElement.addEventListener('scroll', this.scrollListener);
    }
    LazyLoading.prototype.destroy = function () {
        this.items.forEach(function (i) { return i.deactivate(); });
        this.containerElement.removeEventListener('scroll', this.scrollListener);
    };
    /**
     * Set item views
     *
     * All views must be in the "unloaded" state
     */
    LazyLoading.prototype.setItemViews = function (itemViews) {
        var obsoleteActiveViews = new Set();
        // XXX If lazy evaluation was available in our runtime... new Set(this.items.filter(i => i.isActive()).map(i => i.view))
        this.items.forEach(function (i) { if (i.isActive())
            obsoleteActiveViews.add(i.view); });
        var previousItem;
        this.items = itemViews.map(function (view) {
            var wasActive = obsoleteActiveViews.delete(view);
            var item = new LazyItem(view, previousItem);
            item.isLoaded = wasActive;
            previousItem = item;
            return item;
        });
        obsoleteActiveViews.forEach(function (obsoleteView) { return obsoleteView.unload(); });
        this.refreshItems();
    };
    /**
     * Enumerate all views, load every view intersecting the active area, unload the rest.
     * Optionally pre-load a batch of views, if container is at the beginning or at end.
     */
    LazyLoading.prototype.refreshItems = function () {
        var _this = this;
        if (!this.activeRect) {
            this.activeRect = this.calculateActiveRect();
        }
        this.lowMarker = undefined;
        this.highMarker = undefined;
        var lowMarkerFound;
        var highMarkerFound;
        var activeItemsCount = 0;
        var previousItemActive;
        this.items.forEach(function (item) {
            var isActive = _this.manageItem(item);
            if (isActive) {
                ++activeItemsCount;
            }
            if (!lowMarkerFound && isActive) {
                _this.lowMarker = item.previous;
                lowMarkerFound = true;
            }
            if (!highMarkerFound && !isActive && previousItemActive) {
                _this.highMarker = item;
                highMarkerFound = true;
            }
            previousItemActive = isActive;
        });
        if (!this.lowMarker && this.highMarker) {
            // We're at the top. Let's preload the first batch so we don't stutter on the first scroll
            this.highMarker = this.populate(this.highMarker, this.batchSize);
        }
        if (this.debug) {
            console.log("Low marker: " + this.items.indexOf(this.lowMarker) + ", High marker:" + this.items.indexOf(this.highMarker) + ", " + activeItemsCount + " active items");
        }
    };
    LazyLoading.prototype.shouldBeActive = function (item) {
        return this.intersectsActiveArea(item.view.getRect());
    };
    LazyLoading.prototype.intersectsActiveArea = function (itemRect) {
        // Offset this.activeRect by the scroll position
        // FIXME Avoid this calculation on every hit test
        var activeRect = {
            x: this.activeRect.x + this.containerElement.scrollLeft,
            y: this.activeRect.y + this.containerElement.scrollTop,
            width: this.activeRect.width,
            height: this.activeRect.height,
        };
        // TODO Use 1-dimensional geometry here
        // WARNING Rect.intersects has an important difference: it treats touching rectangles as intersecting
        // TODO Avoid producing the intersection rectangle at all
        var intersection = rectIntersection(itemRect, activeRect);
        return intersection != null && intersection.width !== 0 && intersection.height !== 0;
    };
    LazyLoading.prototype.manageItem = function (item) {
        var shouldBeActive = this.shouldBeActive(item);
        if (shouldBeActive) {
            item.activate();
        }
        else {
            item.deactivate();
        }
        return shouldBeActive;
    };
    LazyLoading.prototype.calculateActiveRect = function () {
        var _a = this.containerElement.getBoundingClientRect(), width = _a.width, height = _a.height; // read-only
        var activeRect = { x: 0, y: 0, width: width, height: height };
        // Expand the active area {sensitivity} times to both sides
        // TODO Extract Rect.scale(factor)
        activeRect.x -= activeRect.width * this.sensitivity;
        activeRect.y -= activeRect.height * this.sensitivity;
        activeRect.width *= 1 + this.sensitivity * 2;
        activeRect.height *= 1 + this.sensitivity * 2;
        return activeRect;
    };
    /**
     * Load abs(offset) items starting from the specified one.
     * @param {*} fromItem A view to start from
     * @param {number} offset Specifies the count and direction. A value greater than zero means down.
     * @return Last activated item
     */
    LazyLoading.prototype.populate = function (fromItem, offset) {
        var lastActivated;
        var item = fromItem;
        var counter = Math.abs(offset);
        var direction = offset > 0 ? 1 : -1;
        if (this.debug) {
            console.log("Populate " + Math.abs(offset) + " elements " + (direction > 0 ? 'down' : 'up') + " from " + this.items.indexOf(fromItem));
        }
        while (counter > 0 && item) {
            item.activate();
            lastActivated = item;
            if (!this.shouldBeActive(item)) {
                --counter;
            }
            item = item.successor(direction);
        }
        // Rememeber the pre-fetch direction to avoid trimming it. Should be actually done outside of this method.
        this.lastPrefetchDirection = direction;
        return item || lastActivated;
    };
    /**
     * Unload all items from the specified one until the active area.
     * @param {LazyItem} fromItem  A view to start from
     * @param {number} direction A value greater than zero means down.
     * @return Last deactivated item
     */
    LazyLoading.prototype.trim = function (fromItem, direction) {
        var lastDeactivated;
        var item = fromItem;
        var itemsTrimmed = 0;
        // Track back until the first active active item
        // TODO Extract into a method
        while (item && item.isActive()) {
            var successor = item.successor(-direction);
            if (successor) {
                item = successor;
            }
            else {
                break;
            }
        }
        // Trim down until active area
        while (item && !this.shouldBeActive(item)) {
            item.deactivate();
            lastDeactivated = item;
            ++itemsTrimmed;
            item = item.successor(direction);
        }
        if (this.debug && itemsTrimmed > 0) {
            console.log("Trim " + itemsTrimmed + " items " + (direction > 0 ? 'down' : 'up') + " from " + this.items.indexOf(fromItem));
        }
        return lastDeactivated;
    };
    /**
     * Assess the situation around active area and manage the views using a combination of Trim and Populate.
     * Typically called from a scroll handler.
     */
    LazyLoading.prototype.manageItems = function () {
        if (this.items.length === 0) {
            return;
        }
        var lowMarker = this.lowMarker || this.items[0];
        var highMarker = this.highMarker || this.items[this.items.length - 1];
        var lowMarkerRect = lowMarker.view.getRect();
        var highMarkerRect = highMarker.view.getRect();
        if (lowMarkerRect.x > this.activeRect.x + this.activeRect.width + this.containerElement.scrollLeft
            || lowMarkerRect.y > this.activeRect.y + this.containerElement.scrollTop + this.activeRect.height) {
            this.debug && console.warn('Low marker overshooting detected. Refreshing items');
            this.refreshItems();
            return;
        }
        if (highMarkerRect.x + highMarkerRect.width < this.activeRect.x + this.containerElement.scrollLeft
            || highMarkerRect.y + highMarkerRect.height < this.activeRect.y + this.containerElement.scrollTop) {
            this.debug && console.warn('LazyLoading: High marker overshooting detected. Refreshing items');
            this.refreshItems();
            return;
        }
        if (this.intersectsActiveArea(highMarkerRect) && !highMarker.isActive()) {
            lowMarker = this.trim(lowMarker, 1);
            highMarker = this.populate(highMarker, this.batchSize);
        }
        else if (this.intersectsActiveArea(lowMarkerRect) && !lowMarker.isActive()) {
            highMarker = this.trim(highMarker, -1);
            lowMarker = this.populate(lowMarker, -this.batchSize);
        }
        else if (highMarker.isActive() && this.lastPrefetchDirection !== 1) {
            highMarker = this.trim(highMarker, -1);
        }
        else if (lowMarker.isActive() && this.lastPrefetchDirection !== -1) {
            lowMarker = this.trim(lowMarker, 1);
        }
        if (this.debug && (this.highMarker != highMarker || this.lowMarker != lowMarker)) {
            var low = this.items.indexOf(lowMarker);
            var high = this.items.indexOf(highMarker);
            console.log(high - low + " items loaded (" + low + " to " + high + ")");
        }
        this.highMarker = highMarker;
        this.lowMarker = lowMarker;
    };
    /**
     * Insert new element to manage it by the lazy loading algorithm
     *
     * @param {ItemView} itemView
     * @param {number} atIndex
     */
    LazyLoading.prototype.insertItem = function (itemView, atIndex) {
        var prev = this.items[atIndex - 1];
        var next = this.items[atIndex];
        var item = new LazyItem(itemView, prev);
        if (next) {
            item.next = next;
            next.previous = item;
        }
        this.items.splice(atIndex, 0, item);
        this.manageItem(item);
        // Deactivate one trailing item, if the new one is activated to avoid growing
        // allocated items during repeated insertions
        if (item.isActive()) {
            var lastActive = item;
            while (lastActive.next && lastActive.next.isActive()) {
                lastActive = lastActive.next;
            }
            if (lastActive != item) {
                lastActive.deactivate();
                if (lastActive.next == this.highMarker) {
                    this.highMarker = lastActive;
                }
            }
        }
    };
    /**
     * Replace an element managed by the lazy loading algorithm
     *
     * Simply replacing the element in the parent container does not update the
     * working item set.
     * @param {ItemView} oldItemView
     * @param {ItemView} newItemView
     */
    LazyLoading.prototype.replaceItem = function (oldItemView, newItemView) {
        this.items.forEach(function (item) {
            if (item.view == oldItemView) {
                var active = item.isActive();
                item.deactivate();
                item.view = newItemView;
                if (active) {
                    item.activate();
                }
                else {
                    item.deactivate();
                }
            }
        });
    };
    /**
     * Remove an element from the managed set
     * @param {ItemView} itemView
     */
    LazyLoading.prototype.removeItem = function (itemView) {
        var _this = this;
        this.items = this.items.filter(function (item) {
            if (item.view != itemView) {
                return true;
            }
            if (item.previous) {
                item.previous.next = item.next;
            }
            if (item.next) {
                item.next.previous = item.previous;
            }
            if (_this.lowMarker == item) {
                _this.lowMarker = item.next;
                _this.manageItem(item);
            }
            if (_this.highMarker == item) {
                _this.highMarker = item.previous;
                _this.manageItem(item);
            }
            return false;
        });
    };
    return LazyLoading;
}());
exports.default = LazyLoading;
/**
 * Returns the intersection of two rectangles. Two rectangles intersect if they
 * touch at all, for example, two zero width and height rectangles would
 * intersect if they had the same top and left.
 * Taken from https://github.com/google/closure-library/blob/master/closure/goog/math/rect.js#L159
 */
function rectIntersection(a, b) {
    var x0 = Math.max(a.x, b.x);
    var x1 = Math.min(a.x + a.width, b.x + b.width);
    if (x0 <= x1) {
        var y0 = Math.max(a.y, b.y);
        var y1 = Math.min(a.y + a.height, b.y + b.height);
        if (y0 <= y1) {
            return { left: x0, top: y0, width: x1 - x0, height: y1 - y0 };
        }
    }
    return null;
}
